<template>
	<section class="w-100">
		<div v-for="tariff in visibleTariffs" :key="product.Product + '|' + tariff.Tariff" :class="cssClass(tariff)">
			<div class="d21-product-notAvailable" v-if="tariff.errorMessage">
				<i class="material-icons">error</i>{{ tariff.errorMessage }}
			</div>
			<Plusminus :value="tariff.Count" :disabled="!tariff.IsValid" :minSelect="minSelect" :maxValue="maxCount(product, tariff)" @input="(val) => setPersons(tariff, val)" :hasMaxValueReached="tariff.hasMaxValueReached"/>
			<span class="d21-product-name">{{ V(Reservation.fn.getProduct(product.Product), "Content.Name", "display_name") + " - " + tariff.Info.Content.Name  }}</span>
			<span v-if="tariff.Total !== null" class="d21-product-price">{{ formatPrice(tariff.Total) }}</span>
			<span class="d21-product-age" v-if="hasAgeRestriction(tariff)"><br />{{ tariff.Info.Restrictions.Age.Min }}-{{ tariff.Info.Restrictions.Age.Max + " " + T("years") }}</span>
			<span v-if="tariff.IsValid && tariff.Info.Content.Description" class="d21-product-description" v-clean-html="tariff.Info.Content.Description">
				
			</span>
			<template v-if="hasOccupancyRestriction(tariff)">
				<div v-for="(unit, i) in tariff.Units" class="d21-product-numberOfPersons" :key="'unit_' + i">
					<span>{{ i + 1 + '. ' + T("Ticket_for") + ' ' }}</span>
					<Plusminus :value="tariff.Units[i]" :minValue="tariff.Info.Restrictions.Occupancy.Min" :maxValue="maxCountOccupancy(product, tariff)" @input="(val) => setNumberOfPersons(tariff, i, val)" :hasMaxValueReached="tariff.hasMaxUnitReached"/>
					<span>{{ T("persons" )}}</span>
				</div>
			</template>
		</div>
	</section>
</template>

<script>
	import baseMixin from "@/mixins/base";
	import Plusminus from "@/components/ui/plusminus.vue";
	import validation from "@/scripts/_validation";
	export default {
		mixins: [baseMixin],
		components: {
			Plusminus,
		},
		data() {
			return {
				hasRequiredAdultTickets: null,
				numberOfTickets: {}
			}
		},
		props: {
			product: {
				type: Object,
				required: true,
			}
		},
		computed: {
			minSelect() {
				return this.Channel && this.Channel._oid === 122329 ? 10 : 1;
			},
			visibleTariffs() {
				const tariffs = this.product.Tariffs;
				// no .map(), because we want to keep the reference
				tariffs.forEach(t => {
					t.Info = this.Reservation.fn.getTariff(t.Tariff);
					// display error message for corresponding condition
					if (!t.IsValid && this.hasValidationIssue('PreBookingDeadline', t) && t.Restrictions.BeforeDays) {
						t.errorMessage = this.T("OneTicketing_Tickets_can_be_booked_start") + " " + t.Restrictions.BeforeDays + " " + this.T("days") + " " + this.T("OneTicketing_Tickets_can_be_booked_end");
					} else if (!t.IsValid && this.hasValidationIssue('PreBookingDeadline', t) && t.Restrictions.FromMinutes) {
						t.errorMessage = this.T("OneTicketing_Tickets_can_be_booked_start") + " " + this.getTimeStringFromMinutes(t.Restrictions.FromMinutes) + " " + this.T("OneTicketing_Tickets_can_be_booked_end");
					} else if (!t.IsValid && this.hasValidationIssue('UntilBookingDeadline', t)) {
						t.errorMessage = this.T("OneTicketing_Tickets_can_be_booked_until_start") + " " + this.getTimeStringFromMinutes(t.Restrictions.UntilMinutes) + " " + this.T("OneTicketing_Tickets_can_be_booked_until_end");
					} else if (!this.Reservation.Info.Capacity.AllUnavailable && !t.IsValid && this.hasValidationIssue('NotAvailable', t)) {
						t.errorMessage = this.T("OneTicketing_noTickets_available");
					} else if (!t.IsValid && !this.Reservation.Info.Capacity.AllClosed && this.hasValidationIssue('Closed', t)) {
						t.errorMessage = this.T("OneTicketing_Tickets_closed");
					} else if (this.hasRequiredAdultTickets && t.Count) {
						t.errorMessage = this.T("OneTicketing_Please_select_adult_ticket");
					}
				});

				// if all tariffs are invalid -> display all of them (on purpose)
				const allInvalid = tariffs.every(c => !c.IsValid);
				if (allInvalid) return tariffs;

				// only show valid or closed tariffs
				return tariffs.filter(c => c.IsValid || this.hasValidationIssue('Closed', c));
			},
			isMultiSeason() {
				const product = this.Reservation.fn.getProduct(this.product.Product);
				return product.Settings.Type === "MultiSeason" || product.Settings.Type == "Multi" || product.Settings.Type == "Season";
			},
			selectedTariffs() {
				return this.Reservation.fn.getSelectedTariffs();
			},
			isSelectedMultiSeason() {
				const products = this.Reservation.fn.getSelectedProducts();
				return products.some(p => {
					const productType = this.Reservation.fn.getProduct(p.Product).Settings.Type;
					return productType === "MultiSeason" || productType === "Multi" || productType === "Season"
				});
			},
			isProductSelected() {
				return this.Reservation.fn.getSelectedProducts().some(p => p.Product === this.product.Product);
			},
		},
		methods: {
			isAvailable(tariff) {
				const isTariffSelected = this.selectedTariffs.some(t => t.Tariff === tariff.Tariff)
				const hasSelectedTariffs = this.selectedTariffs.length > 0;
				return !hasSelectedTariffs || (this.isMultiSeason && this.isSelectedMultiSeason && isTariffSelected && this.isProductSelected) || (!this.isSelectedMultiSeason && !this.isMultiSeason);
			},
			cssClass(tariff) {
				let cssClass = "d21-query-products-item";
				const isAvailable = this.isAvailable(tariff);
				if (!tariff.IsValid || !isAvailable) cssClass += " d21-query-products-item--unavailable";
				if (this.Reservation.Discounts.length > 0 && this.Reservation.Discounts[0].isSelected) cssClass += " d21-query-products-item--discounted";
				return cssClass;
			},
			maxCountOccupancy(product, tariff) {
				const max = tariff.Info.Restrictions.Occupancy.Max;

				// cap to product capacity if it's less than this tariff allows
				return Math.min(product.Capacity, max);
			},
			maxCount(product, tariff) {
				if (this.isRestrictionOverPassed('MaxTickets')) return 999;
				const min = tariff.Restrictions.MinCount;

				// maximum count that respects the product capacity
				// e.g. product capacity is 7, but tariff max count would be 8
				// this will cap it at 7
				const maxWithCapacity = Math.floor(product.Capacity / min);
				const maxFromTariff = Math.min(tariff.Restrictions.MaxCount, maxWithCapacity);

				//
				const productInfo = this.Reservation.fn.getProduct(product.Product);
				const maxFromProduct = (productInfo.Settings.Type == "MultiSeason" || productInfo.Settings.Type == "Multi" || productInfo.Settings.Type == "Season") ? 1 : productInfo.Selling && productInfo.Selling.MaxNumberOfTickets ? productInfo.Selling.MaxNumberOfTickets : 1000;
				const maxFromChannel = maxFromProduct !== 1000 ? 1000 : this.Channel && this.Channel._oid === 122329 ? 30 : !this.Channel || this.Channel._oid !== 121153 ? null : 4;

				if (maxFromChannel == null) return Math.min(maxFromProduct, maxFromTariff);
				//
				return Math.min(maxFromProduct, Math.min(maxFromTariff, maxFromChannel));
			},
			setPersons(tariff, selected) {
				tariff.Count = selected;
				let count = selected;
				if(selected == 0) this.$delete(this.numberOfTickets, tariff.Tariff);
				else this.$set(this.numberOfTickets, tariff.Tariff, selected);

				//
				for (var a0 = tariff.Occupancy.length; a0 >= count; a0--) tariff.Occupancy.splice(a0, 1);
				for (var a1 = tariff.Occupancy.length; a1 < count; a1++) {
					tariff.Occupancy.push({
						Person: {
							Givenname: "",
							Lastname: "",
							Communication: {
								Email: null,
								Address: null,
								PostalCode: null,
								City: null
							}
						}
					});
				}

				//
				if (tariff.Info.Restrictions && tariff.Info.Restrictions.Occupancy && tariff.Info.Restrictions.Occupancy.Min > 0) {
					for (var a2 = tariff.Units.length; a2 >= count; a2--) tariff.Units.splice(a2, 1);
					for (var a3 = tariff.Units.length; a3 < count; a3++) tariff.Units.push(tariff.Info.Restrictions.Occupancy.Min);
					const totalUnits = tariff.Units.reduce((acc, val) => acc + val, 0);
					this.$set(this.numberOfTickets, tariff.Tariff, totalUnits);
				}
				this.hasRequiredAdultTickets = this.isAdultTicketRequired();
			},
			isAdultTicketRequired() {
				return validation.isAdultTicketRequired(this.Reservation, this.Channel);
			},
			setNumberOfPersons(tariff, index, selected) {
				tariff.Units[index] = selected;
				const totalUnits = tariff.Units.reduce((acc, val) => acc + val, 0);
				this.$set(this.numberOfTickets, tariff.Tariff, totalUnits);
			},
			hasValidationIssue(key, tariff) {
				return tariff.Restrictions.ValidationIssues && tariff.Restrictions.ValidationIssues.includes(key);
			},
			hasOccupancyRestriction(tariff) {
				return tariff.Count > 0 && tariff.Info.Restrictions && tariff.Info.Restrictions.Occupancy && tariff.Info.Restrictions.Occupancy.Min > 1 && tariff.Info.Restrictions.Occupancy.Min < tariff.Info.Restrictions.Occupancy.Max;
			},
			hasAgeRestriction(tariff) {
				return tariff.IsValid && tariff.Info.Restrictions && tariff.Info.Restrictions.Age && tariff.Info.Restrictions.Age.Min !== null;
			}
		},
		watch:{
			numberOfTickets: {
				deep: true,
				handler(tickets) {
					const sum = Object.values(tickets).reduce((acc, value) => acc + value, 0);
					const hasMaxValueReached = sum >= this.product.Capacity;
					// set if number of selected tickets has reached the product capacity.
					this.visibleTariffs.forEach(t=> {
						this.$set(t, "hasMaxValueReached", hasMaxValueReached);

						if(t.Info.Restrictions.Occupancy && t.Info.Restrictions.Occupancy.Min > 1) {
							// set if there is enough space for next Number of persons on family tickets.
							this.$set(t, "hasMaxValueReached", sum + t.NumberOfPersons > this.product.Capacity);
							this.$set(t, "hasMaxUnitReached", sum >= this.product.Capacity);
						}
					});
				}
			}
		}
	}
</script>